import {
  isAuthenticated,
  isNativeDevice,
} from "@origin-digital/platform-helpers";
import { isOnServer } from "@origin-digital/tal-common";

/**
 * Single source of truth for every package's log-message tag.
 *
 * Before this existed, each package hand-typed its own bracketed prefix
 * inline in every `logger.error/warn/info/debug(...)` call, which drifted
 * into several inconsistent spellings per package over time (e.g. "mesh"
 * alone had five: "[mesh]", "[od/mesh]", "[od/mesh]:", "[od/mesh].",
 * "[@od/mesh]"). Import the relevant key from here instead of writing the
 * bracket string by hand, so every call site for a package shares one
 * definition and can't drift or get misspelled again.
 *
 * House style: "[@od/<name>]" — always @-scoped, never a trailing colon.
 */
export const LOG_TAGS = {
  bootloader: "[@od/bootloader]",
  braze: "[@od/braze]",
  eventRegistry: "[@od/event-reg]",
  mcappRegistry: "[@od/mcapp-reg]",
  mesh: "[@od/mesh]",
  nativeFeatures: "[@od/native-features]",
  originAuth: "[@od/origin-auth]",
  originChat: "[@od/chat]",
  survicate: "[@od/survicate]",
  talClient: "[@od/tal-client]",
} as const;

export type LogTagPackage = keyof typeof LOG_TAGS;

/**
 * Prefixes a package's LOG_TAG with the runtime context every logged message
 * should carry:
 *   - "[server]" when running inside tal-core's SSR environment
 *     (`@origin-digital/tal-common`'s isOnServer() — tal-core evaluates the
 *     browser bundle inside JSDOM, so `window` genuinely exists there;
 *     isNativeDevice()'s `typeof window !== "undefined"` guard alone can't
 *     detect this case, it would fall through to checking JSDOM's fake
 *     `navigator.userAgent` instead) — checked first, since it takes
 *     priority over the native/web distinction below (SSR isn't running on
 *     either device).
 *   - "[native]" when running inside the odin-mobile-app webview.
 *   - "[web]" otherwise.
 * Computed once, synchronously, so each package's logTag.ts picks this up at
 * its own module-load time with no dependency on any later runtime
 * configuration step.
 *
 * Use at the point each package defines its own LOG_TAG, e.g.:
 *   export const LOG_TAG = withDeviceTag(LOG_TAGS.bootloader);
 * — every `logger.error(`${LOG_TAG} ...`)` call site then carries the tag
 * automatically, with no other call sites needing to change.
 */
export const withDeviceTag = (tag: string): string => {
  if (isOnServer()) {
    return `[server] ${tag}`;
  }
  return `${isNativeDevice() ? "[native]" : "[web]"} ${tag}`;
};

/**
 * Whether the current page requires the visitor to be logged in — the
 * `authenticated` flag tal-core resolves from the microsite's page manifest
 * and injects into `window.oetal.pageData` before tal-client's bundle even
 * runs (see packages/bootloader/src/bootloader.ts's IBootProps doc comment:
 * "if the page is for authenticated user, non-public"). This is a per-route
 * classification, not a per-visitor fact — bootloader itself still does a
 * separate session-validity check even when this is true, so it doesn't
 * assert "this visitor is logged in", only "this page is not public".
 *
 * Trusted as already-resolved by tal-core by the time this runs — this
 * doesn't re-derive it, just reads it defensively: SSR-safe (`window` may
 * not exist) and defaults to `false` when the field is absent, matching
 * src/client/setup.ts's own default for the same field.
 *
 * On its own this only tells you "this page requires login" — it does NOT
 * tell you whether the current visitor actually has a session (a visitor can
 * be genuinely logged in while browsing a public page). For gating whether
 * to attempt a JWT/user-context fetch, use shouldAttemptAuthFetch() below
 * instead, which also checks the legacy session cookie.
 */
export const isPageAuthenticated = (): boolean =>
  typeof window !== "undefined" &&
  (window.oetal?.pageData?.authenticated ?? false);

/**
 * Whether it's worth attempting a JWT / user-context fetch on this page
 * load: true if either
 *   - isPageAuthenticated() — this page's manifest requires login, or
 *   - isAuthenticated() (@origin-digital/platform-helpers) — the visitor
 *     already has a `user-token` session cookie, regardless of what this
 *     specific page requires.
 * This is a synchronous heuristic, not a definitive session check. Auth0
 * sessions may exist on public pages without the legacy cookie; callers
 * that need to detect those sessions must fall back to AUTH_USER.
 */
export const shouldAttemptAuthFetch = (): boolean =>
  isPageAuthenticated() || isAuthenticated();
